import { useTranslation } from "react-i18next";
import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAdmin } from "@/hooks/useAdmin";
import { useAuth } from "@/contexts/AuthContext";
import { Bell, UserPlus, CreditCard, Ticket, AlertTriangle, Settings } from "lucide-react";

interface AdminHeaderProps {
  notifications?: { id: string; type: string; message: string; time: string }[];
}

export function AdminHeader({ notifications = [] }: AdminHeaderProps) {
  const navigate = useNavigate();
  const { admin } = useAdmin();
  const { user } = useAuth();
  const [showNotifications, setShowNotifications] = useState(false);
  const notificationRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();

  const hasUnread = notifications.length > 0;

  // Close on click outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (notificationRef.current && !notificationRef.current.contains(event.target as Node)) {
        setShowNotifications(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'user': return <UserPlus className="h-4 w-4 text-[#6FEDD6]" />;
      case 'payment': return <CreditCard className="h-4 w-4 text-[#FFD93D]" />;
      case 'ticket': return <Ticket className="h-4 w-4 text-[#4DA6FF]" />;
      case 'warning': return <AlertTriangle className="h-4 w-4 text-[#FF6B9D]" />;
      default: return <Bell className="h-4 w-4" />;
    }
  };

  return (
    <header className="border-b-2 border-black bg-white">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-6 md:px-8 pl-16 md:pl-6">
        {/* Left: Breadcrumbs or Page Title (Can be dynamic later) */}
        <div className="flex items-center gap-4">
          <h2 className="text-lg font-bold">{t('admin.header.title')}</h2>
        </div>

        {/* Right: Actions & Profile */}
        <div className="flex items-center gap-3">
          {/* Notification Bell */}
          <div className="relative" ref={notificationRef}>
            <button 
              onClick={() => setShowNotifications(!showNotifications)}
              className="relative flex h-10 w-10 items-center justify-center rounded-lg border-2 border-transparent hover:border-black hover:bg-gray-100 transition-all"
            >
              <Bell className="h-5 w-5 text-gray-600" />
              {hasUnread && (
                <span className="absolute right-2 top-2 h-2 w-2 rounded-full bg-red-500 animate-pulse" />
              )}
            </button>

            {/* Notification Dropdown */}
            {showNotifications && (
              <div className="absolute right-0 top-full mt-2 w-80 bg-white border-2 border-black rounded-xl shadow-[4px_4px_0_#1A1A1A] z-50 overflow-hidden">
                <div className="px-4 py-3 border-b-2 border-black bg-gray-50 flex items-center justify-between">
                  <span className="font-black">{t('admin.header.notifications')}</span>
                  {hasUnread && (
                    <span className="text-xs font-bold text-[#FF6B9D]">{t('admin.header.unread', {count: notifications.length})}</span>
                  )}
                </div>
                
                {notifications.length === 0 ? (
                  <div className="p-8 text-center">
                    <Bell className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                    <p className="text-gray-500 font-medium">{t('admin.header.empty')}</p>
                    <p className="text-xs text-gray-400 mt-1">{t('admin.header.emptyDesc')}</p>
                  </div>
                ) : (
                  <div className="max-h-80 overflow-y-auto">
                    {notifications.map((notification) => (
                      <div
                        key={notification.id}
                        className="px-4 py-3 border-b border-gray-100 hover:bg-gray-50 cursor-pointer transition-colors flex items-start gap-3"
                      >
                        <div className="flex h-8 w-8 items-center justify-center rounded-lg border-2 border-black bg-white">
                          {getNotificationIcon(notification.type)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {notification.message}
                          </p>
                          <p className="text-xs text-gray-500">{notification.time}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
          
          {/* User Profile Button - Navigates to Admin Settings */}
          <button 
            onClick={() => navigate('/admin/settings')}
            className="flex items-center gap-2 rounded-full border-2 border-black bg-white pl-1 pr-4 py-1 shadow-[4px_4px_0_#000] hover:translate-x-0.5 hover:translate-y-0.5 hover:shadow-[2px_2px_0_#1A1A1A] transition-all cursor-pointer"
          >
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-[#FF6B9D] font-bold text-white">
              {admin?.role?.name.charAt(0).toUpperCase() || "A"}
            </div>
            <span className="text-sm font-bold">{admin?.role?.display_name || "Admin"}</span>
            <Settings className="h-4 w-4 text-gray-500" />
          </button>
        </div>
      </div>
    </header>
  );
}
